import React, { createRef, useEffect, useState } from 'react';
import { isEmpty } from 'validate.js';

import Input from 'dpl/common/design_system/Input';
import { VERIFICATION_CODE_LENGTH } from 'dpl/contacts/utils/constants';
import { isNumeric } from 'dpl/shared/form/utils';

const SPECIAL_KEYS = {
  DELETE: 'Delete',
  BACKSPACE: 'Backspace',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight'
} as const;

const INITIAL_INPUT_VALUES: string[] = new Array(VERIFICATION_CODE_LENGTH).fill(
  ''
);
const SPECIAL_KEY_VALUES: string[] = Object.values(SPECIAL_KEYS);

interface VerificationCodeInputProps {
  value?: string;
  onChange: (code: string) => void;
}

// Shared 6-box numeric code entry used by the contact-verification quiz and the
// sensitive-change OTP step-up modal.
export default function VerificationCodeInput({
  value: initialCode = '',
  onChange
}: VerificationCodeInputProps) {
  const inputRefs = INITIAL_INPUT_VALUES.map(() =>
    createRef<HTMLInputElement>()
  );
  const [inputValues, setInputValues] =
    useState<string[]>(INITIAL_INPUT_VALUES);

  const code = inputValues.join('');

  function addInputValue(value: string, index: number) {
    setInputValues(
      inputValues.map((inputValue, currentIndex) =>
        currentIndex === index ? value : inputValue
      )
    );
  }

  function removeInputValue(index: number) {
    setInputValues(
      inputValues.map((inputValue, currentIndex) =>
        currentIndex === index ? '' : inputValue
      )
    );
  }

  function focusInput(index: number) {
    inputRefs[index]?.current?.focus();
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>, index: number) {
    const { value } = e.target;

    if (value.length > 1) {
      const pastedValues = [...value]
        .filter(isNumeric)
        .slice(0, VERIFICATION_CODE_LENGTH);

      setInputValues(
        inputValues.map((inputValue, inputIndex) => {
          return pastedValues[inputIndex - index] ?? inputValue;
        })
      );

      focusInput(
        Math.min(index + pastedValues.length, VERIFICATION_CODE_LENGTH - 1)
      );
    } else {
      if (!isNumeric(value)) return;

      addInputValue(value, index);

      if (VERIFICATION_CODE_LENGTH > index) {
        focusInput(index + 1);
      }
    }
  }

  function handleKeyDown(
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) {
    if (SPECIAL_KEY_VALUES.includes(e.key)) {
      e.preventDefault();
    }

    if (isNumeric(e.key) && isNumeric(inputValues[index])) {
      e.preventDefault();
      addInputValue(e.key, index);
      focusInput(index + 1);
    } else if (e.key === SPECIAL_KEYS.BACKSPACE) {
      removeInputValue(index);
      focusInput(index - 1);
    } else if (e.key === SPECIAL_KEYS.DELETE) {
      removeInputValue(index);
    } else if (e.key === SPECIAL_KEYS.ARROW_LEFT) {
      focusInput(index - 1);
    } else if (e.key === SPECIAL_KEYS.ARROW_RIGHT) {
      focusInput(index + 1);
    }
  }

  useEffect(() => {
    onChange(code);
  }, [code]);

  useEffect(() => {
    // Reset input values when initial code is cleared
    if (isEmpty(initialCode)) {
      setInputValues(INITIAL_INPUT_VALUES);
    }
  }, [initialCode]);

  return (
    <div className="VerificationCodeInput flex justify-center gap-8">
      {inputValues.map((value, index) => (
        <Input
          key={index}
          ref={inputRefs[index]}
          className="tc no-select ph0"
          // Using a tel input as an alternative to number, so that
          // the numeric keypad is shown for mobile users
          type="tel"
          value={value}
          onChange={e => handleChange(e, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          style={{
            width: 40,
            // Hide cursor (transparent color) if input is filled
            caretColor: value ? 'transparent' : undefined
          }}
        />
      ))}
    </div>
  );
}
